import { Box, Button, Dialog, DialogActions, Paper, Stack, Typography } from "@mui/material";
import { merchantTableColumns, mearchatSkuDummyData } from "./data";
import { useEffect, useRef, useState } from "react";
import { converAttrToDatagridCol, createdByColumns, selectOptionsKeyName } from "data/globalData";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import axios from "axios";
import { Camera, CameraAltRounded, FileDownload } from "@mui/icons-material";
import DatagridWithApiPagination from "components/ReusableComponents/DatagridWithApiPagination/DatagridWithApiPagination";
import SkuSection from "../CreateShoot/SkuSection/SkuSection";
import { shootStudioSampleDetailsColumns, shootSkuIcodeColumn, shootStudioSampleImgUrlCol } from "../ShootsDashboard/data";
import { set } from "lodash";
import RenderInputField from "components/PimDashboard/CommonComponents/RenderInputField/RenderInputField";
import { grey } from "@mui/material/colors";
import SampleHistoryModal from "./SampleHistoryModal/SampleHistoryModal";
import CameraCapture from "./CameraCapture/CameraCapture";
import { createUpdatedDataPayload } from "utils/createUpdatedDataPayload";
import { custom } from "data/authData";

const StudioSampleInward = () => {
	const { user } = AuthState();
	const { setLoadingOpen, setSnackBarOpen, setSnackBar, snackBar } = GlobalState();

	const disableEdit = user?.role?.[custom.SAMPLE_INWARD_VIEWER.key] === true;

	const [selectedSkus, setSelectedSkus] = useState([]);
	const [allSkus, setAllSkus] = useState([]);
	const [editModal, setEditModal] = useState(false);
	const [historyModal, setHistoryModal] = useState(false);
	const [originalData, setOriginalData] = useState(null);
	const [editedData, setEditedData] = useState(null);
	const [selectedHistorySku, setSelectedHistorySku] = useState(null);
	const [image, setImage] = useState(null);
	const [bulkEdit, setBulkEdit] = useState(false);
	const [cameraActive, setCameraActive] = useState(null);

	useEffect(() => {
		if (selectedSkus.length > 1) setBulkEdit(true);
		else setBulkEdit(false);
	}, [selectedSkus]);

	const handleEditModalClose = () => {
		setEditModal(false);
		setEditedData(null);
		setOriginalData(null);
		setSelectedSkus([]);
		setImage(null);
	};

	const handleIcodeSelect = (data) => {
		setEditedData(data);
		setOriginalData(data);
		setEditModal(true);
	};

	const handleBulkEditButtonClick = () => {
		if (selectedSkus.length === 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select at least one SKU to edit",
				severity: "warning",
			});
			return;
		}
		setEditModal(true);
	};
	const handleDataSave = async (data) => {
		setLoadingOpen(true);
		let imagesArray = image ? [image] : [];
		try {
			let payload = createUpdatedDataPayload(data, originalData, shootStudioSampleDetailsColumns);

			if (!data[shootStudioSampleImgUrlCol.key] && imagesArray.length === 0) {
				setSnackBar({
					...snackBar,
					open: true,
					message: "Please add at least one sample image",
					severity: "warning",
				});
				setLoadingOpen(false);
				return;
			}

			if (imagesArray.length > 0) {
				const formData = new FormData();
				imagesArray.forEach((file) => {
					formData.append("sampleImages", file); // append each file
				});

				// Wait for upload to finish
				const res = await axios.post("/api/studio/upload_sample_images", formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});

				if (!res.data?.result?.length) throw new Error("Image upload failed");

				// Set first image URL in payload (or handle multiple URLs if needed)
				payload[shootStudioSampleImgUrlCol.key] = res.data.result[0].publicUrl;
			}
			if (Object.keys(payload).length === 0) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "No changes made",
					severity: "warning",
				});
				setLoadingOpen(false);
				return;
			}
			if (bulkEdit) {
				await axios.put("/api/studio/bulk_update_shoot_studio_sample_details", {
					body: payload,
					icode_list: selectedSkus,
				});

				// Update local state for bulk
				setAllSkus((prev) =>
					prev.map((item) => (selectedSkus.includes(item[shootSkuIcodeColumn.key]) ? { ...item, ...data } : item))
				);
			} else {
				await axios.put(`/api/studio/update_shoot_studio_sample_details/${data[shootSkuIcodeColumn.key]}`, payload);

				// Update local state for single
				setAllSkus((prev) =>
					prev.map((item) => (item[shootSkuIcodeColumn.key] === data[shootSkuIcodeColumn.key] ? { ...item, ...data } : item))
				);
			}
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Data updated successfully",
				severity: "success",
			});

			setSelectedSkus([]);
			setCameraActive(false);
			handleEditModalClose();
		} catch (err) {
			setSnackBarOpen(true);

			setSnackBar({
				...snackBar,
				severity: "error",
				message: parseErrorMessage(err),
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleHistoryClick = (selectedRow) => {
		setSelectedHistorySku(selectedRow);
		setHistoryModal(true);
	};

	return (
		<Box display="flex" width="90vw" flexWrap="wrap" margin="auto">
			<Box width="100%" alignItems="flex-end" display="flex" justifyContent="flex-end">
				<Button variant="contained" disabled={!bulkEdit} onClick={handleBulkEditButtonClick}>
					Bulk Edit
				</Button>
			</Box>

			<Box pt={1} width="100%">
				<SkuSection
					selectedSkus={selectedSkus}
					setSelectedSkus={setSelectedSkus}
					shootData={{}}
					onRowClick={handleIcodeSelect}
					minHeight={"72vh"}
					maxHeight={"72vh"}
					skusState={allSkus}
					setSkusState={setAllSkus}
					hideSearchHeader={true}
					showActionColumn={true}
					actionList={[{ name: "History", onClick: handleHistoryClick }]}
					disableSelection={disableEdit}
				/>
			</Box>

			<Dialog fullWidth maxWidth="lg" open={editModal} onClose={handleEditModalClose}>
				<Typography variant="h6" p={2} pb={1}>
					{bulkEdit ? "Bulk" : ""}Edit Merchant Sample Details
				</Typography>

				<Box sx={{ p: 2, pt: 1, borderBottom: "1px solid #ccc" }}>
					{bulkEdit ? (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							<Typography variant="h6" fontSize={16} sx={{ width: "100%" }} color={grey[700]}>
								Selected Icodes
							</Typography>
							{selectedSkus.map((sku) => (
								<Box
									key={sku}
									sx={{
										backgroundColor: grey[400],
										borderRadius: "5px",
										padding: "5px",
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}>
									<Typography variant="subtitle1">{sku}</Typography>
								</Box>
							))}
						</Box>
					) : (
						<Typography variant="subtitle1">SKU ICODE: {editedData?.[shootSkuIcodeColumn.key]}</Typography>
					)}
				</Box>

				<Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
					{Object.values(shootStudioSampleDetailsColumns).map((column) => {
						return (
							<RenderInputField
								key={column.key}
								column={column}
								data={editedData}
								setData={setEditedData}
								selectOptObjName={selectOptionsKeyName}
								width="32%"
								longWidth="66%"
							/>
						);
					})}
				</Box>

				{/* IMAGE CAPTURE SECTION */}
				<Stack sx={{ p: 2, pt: 0 }} spacing={2}>
					<Typography variant="h6" fontSize={18} color={grey[700]}>
						Sample Image
					</Typography>

					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "70%" }}>
						{editedData?.[shootStudioSampleImgUrlCol.key] && !cameraActive ? (
							<Box display="flex" columnGap={2}>
								<Paper
									elevation={3}
									sx={{
										width: 500,
										borderRadius: 2,
										overflow: "hidden",
									}}>
									<img src={editedData?.[shootStudioSampleImgUrlCol.key]} alt="Captured" style={{ width: "100%" }} />
								</Paper>
								<Box>
									<Button variant="contained" onClick={() => setCameraActive(true)}>
										Update
									</Button>
								</Box>
							</Box>
						) : (
							<CameraCapture
								currentImgUrl={editedData?.[shootStudioSampleImgUrlCol.key]}
								onCapture={(file) => {
									console.log("File received:", file);
									setImage(file); // your existing uploader logic
								}}
							/>
						)}
					</Box>
				</Stack>

				<DialogActions>
					<Button variant="outlined" onClick={handleEditModalClose}>
						Cancel
					</Button>
					<Button
						autoFocus
						variant="contained"
						sx={{ mr: 1 }}
						onClick={() => handleDataSave(editedData)}
						disabled={disableEdit}>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<SampleHistoryModal skuDetails={selectedHistorySku} modal={historyModal} setModal={setHistoryModal} />
		</Box>
	);
};

export default StudioSampleInward;
